import { useMemo } from 'react';
import { format } from 'date-fns';
// CSI-S4: canonical per-day hours reader. Replaces inline
// `tech.hours?.[dayOfWeek]` reads in both the hook + imperative
// variants below. Null-preserved: missing per-day entry still resolves
// to "tech not working today" (filter excludes); NO flat-column
// fallback (this surface intentionally treats no-hours-configured as
// not-working, matching pre-CSI-S4 semantics). The `getDay` import
// from date-fns is no longer needed — getTechDayHours derives the
// day-of-week internally from the YYYY-MM-DD string.
import { getTechDayHours } from '@/utils/shopTime';

interface TechnicianLike {
  id: string;
  skill_tier: number;
  proficiencies?: Record<string, number | null>;
  efficiency_percentage?: number;
  hours?: Record<number | string, { open?: string; close?: string }>;
  time_off?: string[];
  hire_date?: string | null;
  user_id?: string;
  [key: string]: any;
}

interface UseSortedTechniciansOptions {
  selectedDate: Date;
  selectedDayLabel?: string;
  technicianOrder: string[];
  /** If provided, only show this user's tech column (TECHNICIAN role) */
  restrictToUserId?: string | null;
}

/**
 * Calculates average proficiency across all categories for a technician.
 */
function getAvgProficiency(proficiencies: Record<string, number | null> | undefined): number {
  if (!proficiencies) return 0;
  const values = Object.values(proficiencies).filter((v): v is number => typeof v === 'number');
  return values.length > 0 ? values.reduce((sum, val) => sum + val, 0) / values.length : 0;
}

/**
 * Filter technicians to those working on the selected date,
 * sort by skill_tier desc → avg proficiency desc → efficiency desc,
 * then overlay saved column order.
 *
 * Single source of truth — replaces 6+ inline copies in Dashboard.tsx.
 */
export function useSortedTechnicians<T extends TechnicianLike>(
  technicians: T[],
  { selectedDate, selectedDayLabel, technicianOrder, restrictToUserId }: UseSortedTechniciansOptions
): T[] {
  return useMemo(() => {
    // M-3: format() uses local calendar components, not UTC — safe for UTC+ shops
    const dateStr = selectedDayLabel || format(selectedDate, 'yyyy-MM-dd');

    // 1. Filter: working today, not on time-off
    const filtered = technicians.filter(tech => {
      // Hide technician before their hire date
      if (tech.hire_date && dateStr < tech.hire_date) return false;
      if (tech.time_off?.includes(dateStr)) return false;
      // CSI-S4: canonical per-day hours via getTechDayHours(). Null
      // result preserves the pre-CSI-S4 "no hours → filter out" semantics.
      const dayHours = getTechDayHours(tech, dateStr);
      if (!dayHours?.open || !dayHours?.close) return false;
      if (restrictToUserId) return tech.user_id === restrictToUserId;
      return true;
    });

    // 2. Sort by skill tier → avg proficiency → efficiency
    const sorted = [...filtered].sort((a, b) => {
      if (b.skill_tier !== a.skill_tier) return b.skill_tier - a.skill_tier;
      const avgA = getAvgProficiency(a.proficiencies);
      const avgB = getAvgProficiency(b.proficiencies);
      if (avgB !== avgA) return avgB - avgA;
      return (b.efficiency_percentage || 100) - (a.efficiency_percentage || 100);
    });

    // 3. Overlay saved column order
    if (technicianOrder.length > 0) {
      const ordered = [...sorted];
      ordered.sort((a, b) => {
        const aIdx = technicianOrder.indexOf(a.id);
        const bIdx = technicianOrder.indexOf(b.id);
        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
        if (aIdx !== -1) return -1;
        if (bIdx !== -1) return 1;
        return 0;
      });
      return ordered;
    }

    return sorted;
  }, [technicians, selectedDate, selectedDayLabel, technicianOrder, restrictToUserId]);
}

/**
 * Imperative version for use in event handlers (non-hook contexts).
 * Same logic as the hook but callable anywhere.
 */
export function getSortedTechnicians<T extends TechnicianLike>(
  technicians: T[],
  selectedDate: Date,
  technicianOrder: string[],
  restrictToUserId?: string | null,
  selectedDayLabel?: string
): T[] {
  // M-3: format() uses local calendar components, not UTC — safe for UTC+ shops
  const dateStr = selectedDayLabel || format(selectedDate, 'yyyy-MM-dd');

  const filtered = technicians.filter(tech => {
    // Hide technician before their hire date
    if (tech.hire_date && dateStr < tech.hire_date) return false;
    if (tech.time_off?.includes(dateStr)) return false;
    // CSI-S4: canonical per-day hours via getTechDayHours(); same
    // null-preserved semantics as the hook variant above.
    const dayHours = getTechDayHours(tech, dateStr);
    if (!dayHours?.open || !dayHours?.close) return false;
    if (restrictToUserId) return tech.user_id === restrictToUserId;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (b.skill_tier !== a.skill_tier) return b.skill_tier - a.skill_tier;
    const avgA = getAvgProficiency(a.proficiencies);
    const avgB = getAvgProficiency(b.proficiencies);
    if (avgB !== avgA) return avgB - avgA;
    return (b.efficiency_percentage || 100) - (a.efficiency_percentage || 100);
  });

  if (technicianOrder.length > 0) {
    const ordered = [...sorted];
    ordered.sort((a, b) => {
      const aIdx = technicianOrder.indexOf(a.id);
      const bIdx = technicianOrder.indexOf(b.id);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return 0;
    });
    return ordered;
  }

  return sorted;
}
